// hooks/useRecurrenceWorker.ts
import { useEffect, useState } from 'react'
import { EventDoc, DocType, MasterLabelLabel } from 'types/api/changes'
import { Model } from 'types/model'
import { createRecurrenceWorker } from 'workers/createRecurrenceWorker'
import type { WorkerMessage } from 'workers/groupcalRecurrenceWorker'
import Logger from 'services/Logger'
import Database from 'services/db/Database'
import { prepareTasksForUI } from 'utils/fc_mapping'
import { useAppSettings } from 'components/providers/AppSettingsProvider'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'

interface UseRecurrenceWorkerProps {
  events?: Model<EventDoc>[]
  selectedLabel?: MasterLabelLabel
  showingDocType: DocType
  someDayDate: string
}

export function useRecurrenceWorker({
  events,
  selectedLabel,
  showingDocType,
  someDayDate
}: UseRecurrenceWorkerProps) {
  const { timeZone } = useAppSettings()
  const [processedEvents, setProcessedEvents] = useState<EventDoc[]>([])

  useEffect(() => {
    // Create worker
    const worker = createRecurrenceWorker()
    let cancelled = false

    // Handle messages from worker
    worker.onmessage = (event: MessageEvent) => {
      if (cancelled) return
      let eventsForUI = prepareTasksForUI(
        events?.filter(
          (event) =>
            !event.Recurrence ||
            (event.Recurrence && typeof event.Recurrence === 'string')
        ),
        someDayDate,
        LocalStorage.get(LocalStorageKey.SOMEDAY_ALL_DAY) ?? '',
        undefined,
        showingDocType
      )

      eventsForUI.push(...event.data)
      setProcessedEvents(eventsForUI)
    }

    worker.onerror = (error) => {
      Logger.red('Worker:Error', error)
    }

    // Prepare events for worker
    const forWorkerEvents: EventDoc[] = (
      (events ?? []).filter(
        (event) => event.Recurrence && typeof event.Recurrence !== 'string'
      ) ?? []
    ).map((event) => {
      const cleanDoc = Database.toServerObject(event)
      return {
        ...cleanDoc
      }
    })

    const workerMessage: WorkerMessage = {
      events: forWorkerEvents,
      type: showingDocType,
      label: selectedLabel,
      defaultTz: timeZone
    }

    // Start the worker
    worker.postMessage(workerMessage)

    // Cleanup
    return () => {
      cancelled = true
      worker.terminate()
    }
  }, [events, selectedLabel, showingDocType, someDayDate, timeZone])

  return { processedEvents, setProcessedEvents }
}
